import { discoverUniqueIndexes } from '@erp/shared-utils';
import { normalizeColumnName, type ColumnDraft } from './columnDrafts';
import type { SampledRows } from '../data-assets/sampleForSchema';

/**
 * Key suggestions for the create-a-mapping wizard: the same unique-tuple
 * discovery the mapping detail page runs against stored rows, run here against
 * the rows sampled during "Infer from file".
 *
 * Inference used to hand the first column the key by default, which is only
 * right when that column happens to be unique. Running discovery at inference
 * time instead means the key the wizard proposes is one the file actually
 * supports, and the runners-up become one-click alternatives.
 */

/**
 * Rows fed to the search. The whole point is to answer before the user can
 * notice a pause, and this runs on the main thread — matches the backend's
 * MAX_DISCOVERY_ROWS so both sides judge on comparable evidence.
 */
export const MAX_KEY_SUGGESTION_ROWS = 1000;

/**
 * Largest combination the search will try. Lower than the backend's 4: a key
 * spanning four source columns is not a key a person would pick from a list,
 * and each extra level multiplies the in-browser work.
 */
export const MAX_KEY_SUGGESTION_TUPLE_SIZE = 3;

/** Suggestions offered, most-preferred first. */
export const MAX_KEY_SUGGESTIONS = 5;

export interface KeyCandidate {
  /** Draft ids, in key order — what the wizard's key selection holds. */
  columnIds: string[];
  /** The same columns by name, for display. */
  columnNames: string[];
}

/**
 * Candidate keys the sampled rows support, narrowest first (ties broken by
 * column order, so the leftmost columns win). Empty when there is no sample,
 * too few rows to prove anything, or nothing in the file is unique.
 *
 * Blank cells are treated as nulls, which excludes the column: the resolver
 * drops rows with an incomplete key, so a column with blanks cannot serve as
 * one — the same reason the backend skips null-bearing columns.
 */
export function suggestKeyColumns(
  sample: SampledRows | null,
  columns: readonly ColumnDraft[],
): KeyCandidate[] {
  if (!sample || sample.rows.length === 0) return [];

  const draftByName = new Map<string, ColumnDraft>();
  columns.forEach((column) => {
    const name = column.name.trim().toLowerCase();
    if (name.length > 0) draftByName.set(name, column);
  });

  // Only headers that became a column can be part of a key. Column names are
  // normalized, so the sample's raw headers are normalized the same way to line
  // the two up.
  const searchColumns: string[] = [];
  const sourceIndexes: number[] = [];
  const taken = new Set<string>();
  sample.header.forEach((header, index) => {
    const draft = draftByName.get(normalizeColumnName(header));
    if (!draft) return;
    const name = draft.name.trim();
    // A duplicated header maps onto a column already taken; the second copy is
    // not a distinct column, so it takes no part in the search.
    if (taken.has(name)) return;
    taken.add(name);
    searchColumns.push(name);
    sourceIndexes.push(index);
  });
  if (searchColumns.length === 0) return [];

  const rows = sample.rows.slice(0, MAX_KEY_SUGGESTION_ROWS).map((row) =>
    sourceIndexes.map((index) => {
      const value = (row[index] ?? '').trim();
      return value === '' ? null : value;
    }),
  );

  const { tuples } = discoverUniqueIndexes({
    columns: searchColumns,
    rows,
    // Nothing is declared yet — the whole point is to find what the key
    // should be, so every column is in play.
    keyColumns: [],
    maxTupleSize: MAX_KEY_SUGGESTION_TUPLE_SIZE,
  });

  const orderByName = new Map(
    searchColumns.map((name, index) => [name, index] as const),
  );
  return [...tuples]
    .sort((a, b) => {
      if (a.length !== b.length) return a.length - b.length;
      for (let i = 0; i < a.length; i += 1) {
        const diff =
          (orderByName.get(a[i]) ?? 0) - (orderByName.get(b[i]) ?? 0);
        if (diff !== 0) return diff;
      }
      return 0;
    })
    .slice(0, MAX_KEY_SUGGESTIONS)
    .flatMap((tuple) => {
      const drafts = tuple
        .map((name) => draftByName.get(name.trim().toLowerCase()))
        .filter((draft) => draft !== undefined);
      // A tuple naming a column that is no longer a draft describes a key the
      // wizard cannot apply, so it is not offered at all.
      if (drafts.length !== tuple.length) return [];
      return [
        {
          columnIds: drafts.map((draft) => draft.id),
          columnNames: drafts.map((draft) => draft.name),
        },
      ];
    });
}

/**
 * The candidates that still describe a key the wizard could apply: every column
 * still exists and still carries the name discovery proved unique under.
 *
 * A removed column leaves a key that cannot be selected; a renamed one leaves a
 * badge naming a column that no longer exists and a claim about a file column
 * that may no longer be the one the rename points at. Either way the proof no
 * longer holds, so the option stops being offered rather than being offered
 * wrong.
 */
export function liveKeyCandidates(
  candidates: readonly KeyCandidate[],
  columns: readonly ColumnDraft[],
): KeyCandidate[] {
  const nameById = new Map(
    columns.map((column) => [column.id, column.name.trim()] as const),
  );
  return candidates.filter((candidate) =>
    // No length guard: columnIds and columnNames are two views of one tuple,
    // built together in suggestKeyColumns and never mutated apart, so a length
    // check here would be a branch that can never be taken.
    // react-doctor-disable-next-line react-doctor/js-length-check-first
    candidate.columnIds.every(
      (id, index) => nameById.get(id) === candidate.columnNames[index].trim(),
    ),
  );
}

/** True when the candidate is exactly the current key selection, in any order. */
export function isCandidateSelected(
  candidate: KeyCandidate,
  keyColumnIds: readonly string[],
): boolean {
  if (candidate.columnIds.length !== keyColumnIds.length) return false;
  const selected = new Set(keyColumnIds);
  return candidate.columnIds.every((id) => selected.has(id));
}
